import {
    ChangeDetectionStrategy,
    Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output,
    ViewChild
} from '@angular/core';
import {EventsService} from '@services/events.service';

@Component({
    selector: 'app-modal',
    exportAs: 'modal',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./modal.component.scss'],
    templateUrl: './modal.component.html'
})
export class ModalComponent implements OnDestroy, OnInit {

    /**
     * Будет ли модалка большой?
     * @type {boolean}
     */
    @Input() large = false;

    /**
     * Активна (Открыта) по-умолчанию при инициализации?
     */
    @Input() active = false;

    /**
     * Возможность жестко задать ширину модалки в пикселях
     */
    @Input() width: number;

    /**
     * Заголовок модалки;
     */
    @Input() title = '&nbsp;';

    /**
     * Предотвратить закрытие модального окна
     */
    @Input() preventClose = false;

    /**
     * Отобразить прелоадер модального окна?
     */
    @Input() showPreloader = false;

    /**
     * Закрывать модалку только по клику на кнопку закрытия, без возможности закрыть по ESC или по клику вне модалки
     */
    @Input() closeByButtonOnly = false;

    /**
     * Показывать ли заголовок модального окна?
     */
    @Input() showTitle = true;

    @Output() closed = new EventEmitter();
    @Output() openedTriggered = new EventEmitter();

    @ViewChild('modal') private modal: ElementRef;

    public opened: boolean;

    constructor(
        private elementRef: ElementRef,
        private events: EventsService,
    ) {}

    /**
     * Перенести модалку из ее текущего местоположения в document,
     * чтобы несколько модалок отркывались рядом друг с другом, а не внутри одна другой.
     * Тем самым победили ограничение размеров вложенной модалки размерами родительской.
     */
    private moveModalToDocumentRoot() {
        const native = this.elementRef.nativeElement;
        document.body.appendChild(native);
    }

    ngOnInit() {
        this.moveModalToDocumentRoot();

        if (this.active) {
            this.open();
        }
    }

    /**
     * Закрываем модалку при нажатии клавиши ESC
     * @param event
     */
    @HostListener('document:keydown.esc', ['$event'])
    private onKeyDown(event) {
        if (this.closeByButtonOnly || this.showPreloader) {
            return;
        }
        /**
         * Закрываем последнюю открытую модалку (самую верхнюю) и только ее.
         * Для этого по нажатию ESC модалка будет сарвнивать последнюю открытую с собой и закрытвать себя,
         * если она и есть последняя открытая.
         * Из-за изначальной вложенности модалок каждая вложенная будет в массиве после своего родителя,
         * поэтому такая проверка хоть и не красива, но имеет место быть.
         * @type {NodeListOf<Element>}
         */
        const openedModals = document.querySelectorAll('.app-modal-container:not([aria-hidden])');
        if (openedModals[openedModals.length - 1] === this.modal.nativeElement) {
            this.close();
        }
    }

    @HostListener('click', ['$event'])
    private onClick(event) {
        if (this.closeByButtonOnly || this.showPreloader) {
            return;
        }

        if (event.target === this.modal.nativeElement) {
            this.close();
        }
    }

    open() {
        this.modal.nativeElement.removeAttribute('aria-hidden');
        document.body.classList.add('modal-open');
        this.opened = true;
        this.openedTriggered.emit();
    }

    close() {
        if (this.preventClose) {
            this.events.showNoty({
                id: 'modal-prevent-message',
                type: 'info',
                content: 'Дождитесь окончания выполнения операции'
            });
            return;
        }

        this.modal.nativeElement.setAttribute('aria-hidden', 'true');
        this.opened = false;
        this.closed.emit();

        this.checkBodyStatus();
    }

    ngOnDestroy() {
        this.modal.nativeElement.setAttribute('aria-hidden', 'true');
        this.opened = false;
        this.checkBodyStatus();

        /**
         * А поскольку мы ручками перетаскивали модалку в body,
         * то надо почистить за собой, так как верстка теперь сама не удалится.
         */
        if (this.elementRef.nativeElement.parentNode === document.body) {
            document.body.removeChild(this.elementRef.nativeElement);
        }

        this.events.hideNoty('modal-prevent-message');
    }

    private checkBodyStatus() {
        /* Если есть еще открытые модалки, то body все еще остается фиксированным */
        const openedModals = document.querySelectorAll('.app-modal-container:not([aria-hidden])');
        if (openedModals.length === 0) {
            document.body.classList.remove('modal-open');
        }
    }
}
